/**
 * Lit Adapter - Consolidated Web Components integration for Directive
 *
 * Controllers: DerivedController, FactController,
 * InspectController (with throttle), RequirementStatusController,
 * DirectiveSelectorController,
 * WatchController (with fact mode), SystemController,
 * ExplainController, ConstraintStatusController, OptimisticUpdateController, ModuleController
 *
 * Factories: createDerived, createFact, createInspect,
 * createRequirementStatus, createWatch,
 * createDirectiveSelector, useDispatch, useEvents, useHistory,
 * getDerived, getFact, createTypedHooks, shallowEqual
 */

import type {
  CreateSystemOptionsSingle,
  ErrorBoundaryConfig,
  HistoryState,
  InferDerivations,
  InferEvents,
  InferFacts,
  InferSelectorState,
  ModuleDef,
  ModuleSchema,
  ModulesMap,
  NamespacedSystem,
  Plugin,
  SingleModuleSystem,
  SystemSnapshot,
  TraceOption,
} from "@directive-run/core";
import {
  createRequirementStatusPlugin,
  createSystem,
} from "@directive-run/core";
import type { RequirementTypeStatus } from "@directive-run/core";
import {
  type ConstraintInfo,
  type InspectState,
  type TrackedSelectorResult,
  assertSystem,
  buildHistoryState,
  computeInspectState,
  createThrottle,
  defaultEquality,
  depsChanged,
  mergeHydrationFacts,
  runTrackedSelector,
  shallowEqual,
} from "@directive-run/core/adapter-utils";
import type { ReactiveController, ReactiveControllerHost } from "lit";
import isDevelopment from "#is-development";

// Re-export for convenience
export type { RequirementTypeStatus, InspectState, ConstraintInfo };
export { shallowEqual };

/** Type for the requirement status plugin return value */
export type StatusPlugin = ReturnType<typeof createRequirementStatusPlugin>;

// ============================================================================
// Context
// ============================================================================

/**
 * Context key for Directive system.
 * Use with @lit/context for dependency injection across shadow DOM boundaries.
 */
export const directiveContext = Symbol("directive");

// ============================================================================
// Base Controller
// ============================================================================

/**
 * Base controller that manages system subscription lifecycle.
 */
abstract class DirectiveController implements ReactiveController {
  protected host: ReactiveControllerHost;
  protected system: SingleModuleSystem<any>;
  protected unsubscribe?: () => void;

  constructor(host: ReactiveControllerHost, system: SingleModuleSystem<any>) {
    this.host = host;
    this.system = system;
    host.addController(this);
  }

  hostConnected(): void {
    this.subscribe();
  }

  hostDisconnected(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  protected abstract subscribe(): void;

  protected requestUpdate(): void {
    this.host.requestUpdate();
  }
}

// ============================================================================
// Core Controllers
// ============================================================================

/**
 * Reactive controller for derivations.
 * Accepts a single key (string) or an array of keys (string[]).
 * - Single key: `.value` returns `T`
 * - Array of keys: `.value` returns `Record<string, unknown>`
 */
export class DerivedController<T> extends DirectiveController {
  private keys: string[];
  private isMulti: boolean;
  value: T;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    key: string | string[],
  ) {
    super(host, system);
    this.isMulti = Array.isArray(key);
    this.keys = this.isMulti ? (key as string[]) : [key as string];
    this.value = this.getValues();

    if (isDevelopment) {
      if (!this.isMulti && this.value === undefined) {
        console.warn(
          `[Directive] DerivedController("${this.keys[0]}") returned undefined. ` +
            `Check that "${this.keys[0]}" is defined in your module's derive property.`,
        );
      }
    }
  }

  private getValues(): T {
    if (this.isMulti) {
      const result: Record<string, unknown> = {};
      for (const id of this.keys) {
        result[id] = this.system.read(id);
      }
      return result as T;
    }
    return this.system.read(this.keys[0]!) as T;
  }

  protected subscribe(): void {
    this.value = this.getValues();
    this.unsubscribe = this.system.subscribe(this.keys, () => {
      this.value = this.getValues();
      this.requestUpdate();
    });
  }
}

/**
 * Reactive controller for a single fact value.
 */
export class FactController<T> extends DirectiveController {
  private factKey: string;
  value: T | undefined;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    factKey: string,
  ) {
    super(host, system);
    this.factKey = factKey;
    this.value = system.facts.$store.get(factKey) as T | undefined;

    if (isDevelopment) {
      if (!system.facts.$store.has(factKey)) {
        console.warn(
          `[Directive] FactController("${factKey}") — fact not found in store. ` +
            `Check that "${factKey}" is defined in your module's schema.`,
        );
      }
    }
  }

  protected subscribe(): void {
    this.value = this.system.facts.$store.get(this.factKey) as T | undefined;
    this.unsubscribe = this.system.facts.$store.subscribe(
      [this.factKey],
      () => {
        this.value = this.system.facts.$store.get(this.factKey) as
          | T
          | undefined;
        this.requestUpdate();
      },
    );
  }
}

/**
 * Consolidated inspection controller.
 * Returns InspectState with optional throttling.
 */
export class InspectController extends DirectiveController {
  value: InspectState;
  private throttleMs: number;
  private throttleCleanup?: () => void;
  private unsubSettled?: () => void;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    options?: { throttleMs?: number },
  ) {
    super(host, system);
    this.throttleMs = options?.throttleMs ?? 0;
    this.value = computeInspectState(system);
  }

  protected subscribe(): void {
    this.value = computeInspectState(this.system);

    const update = () => {
      this.value = computeInspectState(this.system);
      this.requestUpdate();
    };

    if (this.throttleMs > 0) {
      const { throttled, cleanup } = createThrottle(update, this.throttleMs);
      this.throttleCleanup = cleanup;
      this.unsubscribe = this.system.facts.$store.subscribeAll(throttled);
      this.unsubSettled = this.system.onSettledChange(throttled);
    } else {
      this.unsubscribe = this.system.facts.$store.subscribeAll(update);
      this.unsubSettled = this.system.onSettledChange(update);
    }
  }

  hostDisconnected(): void {
    this.throttleCleanup?.();
    this.unsubSettled?.();
    super.hostDisconnected();
  }
}

/**
 * Reactive controller for requirement status.
 */
export class RequirementStatusController implements ReactiveController {
  private host: ReactiveControllerHost;
  private statusPlugin: StatusPlugin;
  private type: string;
  private unsubscribe?: () => void;
  value: RequirementTypeStatus;

  constructor(
    host: ReactiveControllerHost,
    statusPlugin: StatusPlugin,
    type: string,
  ) {
    this.host = host;
    this.statusPlugin = statusPlugin;
    this.type = type;
    this.value = statusPlugin.getStatus(type);
    host.addController(this);
  }

  hostConnected(): void {
    this.value = this.statusPlugin.getStatus(this.type);
    this.unsubscribe = this.statusPlugin.subscribe(() => {
      this.value = this.statusPlugin.getStatus(this.type);
      this.host.requestUpdate();
    });
  }

  hostDisconnected(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }
}

// ============================================================================
// Selector Controllers
// ============================================================================

/**
 * Reactive controller for selecting across all facts.
 * Uses `withTracking()` for auto-tracking when constructed with `autoTrack: true`.
 */
export class DirectiveSelectorController<
  S extends ModuleSchema = ModuleSchema,
  R = unknown,
> extends DirectiveController {
  private selector: (state: InferSelectorState<S>) => R;
  private equalityFn: (a: R, b: R) => boolean;
  private autoTrack: boolean;
  private deriveKeySet: Set<string>;
  private trackedFactKeys: string[] = [];
  private trackedDeriveKeys: string[] = [];
  private unsubs: Array<() => void> = [];
  value: R;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<S>,
    selector: (state: InferSelectorState<S>) => R,
    equalityFn: (a: R, b: R) => boolean = defaultEquality,
    options?: { autoTrack?: boolean },
  ) {
    super(host, system);
    this.selector = selector;
    this.equalityFn = equalityFn;
    this.autoTrack = options?.autoTrack ?? true;
    this.deriveKeySet = new Set(Object.keys(system.derive ?? {}));

    const initial = this.runWithTracking();
    this.value = initial.value;
    this.trackedFactKeys = initial.factKeys;
    this.trackedDeriveKeys = initial.deriveKeys;
  }

  private runWithTracking(): TrackedSelectorResult<R> {
    return runTrackedSelector(
      this.system,
      this.deriveKeySet,
      this.selector as (state: Record<string, unknown>) => R,
    );
  }

  private resubscribe(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];

    const onUpdate = () => {
      const result = this.runWithTracking();
      if (!this.equalityFn(this.value, result.value)) {
        this.value = result.value;
        this.requestUpdate();
      }
      if (this.autoTrack) {
        // Re-track: check if deps changed
        if (
          depsChanged(
            this.trackedFactKeys,
            result.factKeys,
            this.trackedDeriveKeys,
            result.deriveKeys,
          )
        ) {
          this.trackedFactKeys = result.factKeys;
          this.trackedDeriveKeys = result.deriveKeys;
          this.resubscribe();
        }
      }
    };

    if (this.autoTrack) {
      if (this.trackedFactKeys.length > 0) {
        this.unsubs.push(
          this.system.facts.$store.subscribe(this.trackedFactKeys, onUpdate),
        );
      } else if (this.trackedDeriveKeys.length === 0) {
        this.unsubs.push(this.system.facts.$store.subscribeAll(onUpdate));
      }
      if (this.trackedDeriveKeys.length > 0) {
        this.unsubs.push(
          this.system.subscribe(this.trackedDeriveKeys, onUpdate),
        );
      }
    } else {
      this.unsubs.push(this.system.facts.$store.subscribeAll(onUpdate));
    }
  }

  protected subscribe(): void {
    const result = this.runWithTracking();
    this.value = result.value;
    this.trackedFactKeys = result.factKeys;
    this.trackedDeriveKeys = result.deriveKeys;
    this.resubscribe();
  }

  hostDisconnected(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];
    super.hostDisconnected();
  }
}

/**
 * Reactive controller that watches a fact or derivation and calls a callback on change.
 * The key is auto-detected — works with both fact keys and derivation keys.
 */
export class WatchController<T> extends DirectiveController {
  private key: string;
  private callback: (newValue: T, previousValue: T | undefined) => void;

  /** Watch a derivation or fact by key (auto-detected). When a key exists in both facts and derivations, the derivation overload takes priority. */
  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    key: string,
    callback: (newValue: T, previousValue: T | undefined) => void,
  ) {
    super(host, system);
    this.key = key;
    this.callback = callback;
  }

  protected subscribe(): void {
    this.unsubscribe = this.system.watch<T>(this.key, this.callback);
  }
}

// ============================================================================
// New Controllers
// ============================================================================

/**
 * Reactive controller for requirement explanations.
 */
export class ExplainController extends DirectiveController {
  private requirementId: string;
  value: string | null;
  private unsubSettled?: () => void;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    requirementId: string,
  ) {
    super(host, system);
    this.requirementId = requirementId;
    this.value = system.explain(requirementId);
  }

  protected subscribe(): void {
    this.value = this.system.explain(this.requirementId);

    const update = () => {
      this.value = this.system.explain(this.requirementId);
      this.requestUpdate();
    };

    this.unsubscribe = this.system.facts.$store.subscribeAll(update);
    this.unsubSettled = this.system.onSettledChange(update);
  }

  hostDisconnected(): void {
    this.unsubSettled?.();
    super.hostDisconnected();
  }
}

/**
 * Reactive controller for constraint status.
 */
export class ConstraintStatusController extends DirectiveController {
  private constraintId?: string;
  value: ConstraintInfo[] | ConstraintInfo | null;
  private unsubSettled?: () => void;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    constraintId?: string,
  ) {
    super(host, system);
    this.constraintId = constraintId;
    this.value = this.getVal();
  }

  private getVal(): ConstraintInfo[] | ConstraintInfo | null {
    const inspection = this.system.inspect();
    if (!this.constraintId) return inspection.constraints;
    return (
      inspection.constraints.find(
        (c: ConstraintInfo) => c.id === this.constraintId,
      ) ?? null
    );
  }

  protected subscribe(): void {
    this.value = this.getVal();

    const update = () => {
      this.value = this.getVal();
      this.requestUpdate();
    };

    this.unsubscribe = this.system.facts.$store.subscribeAll(update);
    this.unsubSettled = this.system.onSettledChange(update);
  }

  hostDisconnected(): void {
    this.unsubSettled?.();
    super.hostDisconnected();
  }
}

/**
 * Reactive controller for optimistic updates.
 */
export class OptimisticUpdateController implements ReactiveController {
  private host: ReactiveControllerHost;
  private system: SingleModuleSystem<any>;
  private statusPlugin?: StatusPlugin;
  private requirementType?: string;
  private snapshot: SystemSnapshot | null = null;
  private statusUnsub: (() => void) | null = null;

  isPending = false;
  error: Error | null = null;

  constructor(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<any>,
    statusPlugin?: StatusPlugin,
    requirementType?: string,
  ) {
    this.host = host;
    this.system = system;
    this.statusPlugin = statusPlugin;
    this.requirementType = requirementType;
    host.addController(this);
  }

  hostConnected(): void {}

  hostDisconnected(): void {
    this.statusUnsub?.();
    this.statusUnsub = null;
  }

  rollback(): void {
    if (this.snapshot) {
      this.system.restore(this.snapshot);
      this.snapshot = null;
    }
    this.isPending = false;
    this.error = null;
    this.statusUnsub?.();
    this.statusUnsub = null;
    this.host.requestUpdate();
  }

  mutate(updateFn: () => void): void {
    this.snapshot = this.system.getSnapshot();
    this.isPending = true;
    this.error = null;
    this.system.batch(updateFn);
    this.host.requestUpdate();

    if (this.statusPlugin && this.requirementType) {
      this.statusUnsub?.();
      this.statusUnsub = this.statusPlugin.subscribe(() => {
        const status = this.statusPlugin!.getStatus(this.requirementType!);
        if (!status.isLoading && !status.hasError) {
          this.snapshot = null;
          this.isPending = false;
          this.statusUnsub?.();
          this.statusUnsub = null;
          this.host.requestUpdate();
        } else if (status.hasError) {
          this.error = status.lastError;
          this.rollback();
        }
      });
    }
  }
}

/**
 * Reactive controller that creates and manages a Directive system.
 * The system is automatically started when the host connects and destroyed when it disconnects.
 */
export class SystemController<M extends ModuleSchema>
  implements ReactiveController
{
  private options: ModuleDef<M> | CreateSystemOptionsSingle<M>;
  private _system: SingleModuleSystem<M> | null = null;

  constructor(
    host: ReactiveControllerHost,
    options: ModuleDef<M> | CreateSystemOptionsSingle<M>,
  ) {
    this.options = options;
    host.addController(this);
  }

  get system(): SingleModuleSystem<M> {
    if (!this._system) {
      throw new Error(
        "[Directive] SystemController.system is not available. " +
          "This can happen if:\n" +
          "  1. Accessed before hostConnected (e.g., in a class field initializer)\n" +
          "  2. Accessed after hostDisconnected (system was destroyed)\n" +
          "Solution: Access system only in lifecycle methods (connectedCallback, render) " +
          "or after the element is connected to the DOM.",
      );
    }
    return this._system;
  }

  hostConnected(): void {
    const isModule = "id" in this.options && "schema" in this.options;
    const system = isModule
      ? createSystem({ module: this.options as ModuleDef<M> })
      : createSystem(this.options as CreateSystemOptionsSingle<M>);
    this._system = system as unknown as SingleModuleSystem<M>;
    this._system.start();
  }

  hostDisconnected(): void {
    // RFC 0009 follow-up (R18 Tier 2-B): destroyAsync so source
    // unsubscribes complete; fire-and-forget with swallow-catch
    // (Lit's hostDisconnected is sync).
    this._system?.destroyAsync().catch((err: unknown) => {
      if (isDevelopment)
        console.warn("[Directive] destroyAsync rejected during unmount:", err);
    });
    this._system = null;
  }
}

/**
 * Module controller — zero-config all-in-one.
 * Creates system, starts it, subscribes to all facts/derivations.
 */
export class ModuleController<M extends ModuleSchema>
  implements ReactiveController
{
  private host: ReactiveControllerHost;
  private moduleDef: ModuleDef<M>;
  private config?: {
    plugins?: Plugin<any>[];
    trace?: TraceOption;
    errorBoundary?: ErrorBoundaryConfig;
    tickMs?: number;
    zeroConfig?: boolean;
    initialFacts?: Record<string, any>;
    status?: boolean;
  };

  private _system: SingleModuleSystem<M> | null = null;
  private unsubFacts?: () => void;
  private unsubDerived?: () => void;

  facts: InferFacts<M> = {} as InferFacts<M>;
  derived: InferDerivations<M> = {} as InferDerivations<M>;
  statusPlugin?: StatusPlugin;

  get system(): SingleModuleSystem<M> {
    if (!this._system) {
      throw new Error(
        "[Directive] ModuleController.system is not available before hostConnected.",
      );
    }
    return this._system;
  }

  get events(): SingleModuleSystem<M>["events"] {
    return this.system.events;
  }

  constructor(
    host: ReactiveControllerHost,
    moduleDef: ModuleDef<M>,
    config?: {
      plugins?: Plugin<any>[];
      trace?: TraceOption;
      errorBoundary?: ErrorBoundaryConfig;
      tickMs?: number;
      zeroConfig?: boolean;
      initialFacts?: Record<string, any>;
      status?: boolean;
    },
  ) {
    this.host = host;
    this.moduleDef = moduleDef;
    this.config = config;
    host.addController(this);
  }

  hostConnected(): void {
    const allPlugins = [...(this.config?.plugins ?? [])];

    if (this.config?.status) {
      const sp = createRequirementStatusPlugin();
      this.statusPlugin = sp;
      allPlugins.push(sp.plugin as Plugin<any>);
    }

    const system = createSystem({
      module: this.moduleDef,
      plugins: allPlugins.length > 0 ? allPlugins : undefined,
      trace: this.config?.trace,
      errorBoundary: this.config?.errorBoundary,
      tickMs: this.config?.tickMs,
      zeroConfig: this.config?.zeroConfig,
      initialFacts: this.config?.initialFacts,
    } as any) as unknown as SingleModuleSystem<M>;

    this._system = system;
    system.start();

    // Subscribe to all facts
    this.facts = system.facts.$store.toObject() as InferFacts<M>;
    this.unsubFacts = system.facts.$store.subscribeAll(() => {
      this.facts = system.facts.$store.toObject() as InferFacts<M>;
      this.host.requestUpdate();
    });

    // Subscribe to all derivations
    const derivationKeys = Object.keys(system.derive ?? {});
    const getDerived = (): InferDerivations<M> => {
      const result: Record<string, unknown> = {};
      for (const key of derivationKeys) {
        result[key] = system.read(key);
      }
      return result as InferDerivations<M>;
    };
    this.derived = getDerived();

    if (derivationKeys.length > 0) {
      this.unsubDerived = system.subscribe(derivationKeys, () => {
        this.derived = getDerived();
        this.host.requestUpdate();
      });
    }
  }

  hostDisconnected(): void {
    this.unsubFacts?.();
    this.unsubDerived?.();
    // follow-up — R18 Tier 2-B switched `SystemController` and
    // `DirectiveQueryController` to `destroyAsync` but missed the
    // zero-config `ModuleController` (this class). Same rationale:
    // source unsubscribes are async (Supabase, MCP); sync destroy
    // would drop those Promises on the floor and leave brokers
    // holding ghost subscriptions until heartbeat. Fire-and-forget
    // with a swallow-catch — Lit's hostDisconnected is sync.
    this._system?.destroyAsync().catch((err: unknown) => {
      if (isDevelopment)
        console.warn("[Directive] destroyAsync rejected during unmount:", err);
    });
    this._system = null;
  }

  dispatch(event: InferEvents<M>): void {
    this.system.dispatch(event);
  }
}

// ============================================================================
// Factory Functions (active)
// ============================================================================

export function createDerived<T>(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  key: string | string[],
): DerivedController<T> {
  return new DerivedController<T>(host, system, key);
}

export function createFact<T>(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  factKey: string,
): FactController<T> {
  return new FactController<T>(host, system, factKey);
}

/**
 * Create an inspect controller.
 * Returns InspectState; pass `{ throttleMs }` for throttled updates.
 */
export function createInspect(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  options?: { throttleMs?: number },
): InspectController {
  return new InspectController(host, system, options);
}

export function createRequirementStatus(
  host: ReactiveControllerHost,
  statusPlugin: StatusPlugin,
  type: string,
): RequirementStatusController {
  return new RequirementStatusController(host, statusPlugin, type);
}

export function createWatch<T>(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  derivationId: string,
  callback: (newValue: T, previousValue: T | undefined) => void,
): WatchController<T> {
  return new WatchController<T>(host, system, derivationId, callback);
}

export function createDirectiveSelector<S extends ModuleSchema, R>(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<S>,
  selector: (state: InferSelectorState<S>) => R,
  equalityFn: (a: R, b: R) => boolean = defaultEquality,
  options?: { autoTrack?: boolean },
): DirectiveSelectorController<S, R> {
  return new DirectiveSelectorController<S, R>(
    host,
    system,
    selector,
    equalityFn,
    options,
  );
}

export function createExplain(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  requirementId: string,
): ExplainController {
  return new ExplainController(host, system, requirementId);
}

export function createConstraintStatus(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  constraintId?: string,
): ConstraintStatusController {
  return new ConstraintStatusController(host, system, constraintId);
}

export function createOptimisticUpdate(
  host: ReactiveControllerHost,
  system: SingleModuleSystem<any>,
  statusPlugin?: StatusPlugin,
  requirementType?: string,
): OptimisticUpdateController {
  return new OptimisticUpdateController(
    host,
    system,
    statusPlugin,
    requirementType,
  );
}

/**
 * Build a `ModuleController` from a Directive module definition.
 *
 * The function is exported as both `createModuleController` (preferred)
 * and `createModule` (legacy alias). The original `createModule` name
 * collides with `@directive-run/core`'s `createModule` — importing
 * both into the same scope shadows whichever lands last in editor
 * auto-import order. New code should use `createModuleController`;
 * the `createModule` alias remains so existing imports still resolve.
 */
export function createModuleController<M extends ModuleSchema>(
  host: ReactiveControllerHost,
  moduleDef: ModuleDef<M>,
  config?: {
    plugins?: Plugin<any>[];
    trace?: TraceOption;
    errorBoundary?: ErrorBoundaryConfig;
    tickMs?: number;
    zeroConfig?: boolean;
    initialFacts?: Record<string, any>;
    status?: boolean;
  },
): ModuleController<M> {
  return new ModuleController<M>(host, moduleDef, config);
}

/**
 * Legacy alias for `createModuleController`. Collides by name with
 * `@directive-run/core`'s `createModule`. New code should prefer
 * `createModuleController`.
 *
 * @deprecated Renamed to `createModuleController` — same shape, no
 * import collision with `@directive-run/core`. The function-wrapper
 * shape (vs `export const x = y`) ensures the `@deprecated` JSDoc
 * tag renders the strikethrough in every TS-aware editor.
 */
export function createModule<M extends ModuleSchema>(
  host: ReactiveControllerHost,
  moduleDef: ModuleDef<M>,
  config?: Parameters<typeof createModuleController<M>>[2],
): ModuleController<M> {
  return createModuleController<M>(host, moduleDef, config);
}

// ============================================================================
// Functional Helpers
// ============================================================================

export function useDispatch<M extends ModuleSchema = ModuleSchema>(
  system: SingleModuleSystem<M>,
): (event: InferEvents<M>) => void {
  assertSystem("useDispatch", system);
  return (event: InferEvents<M>) => {
    system.dispatch(event);
  };
}

/**
 * Returns the system's events dispatcher.
 */
export function useEvents<M extends ModuleSchema = ModuleSchema>(
  system: SingleModuleSystem<M>,
): SingleModuleSystem<M>["events"] {
  assertSystem("useEvents", system);
  return system.events;
}

/**
 * Reactive controller for history state.
 * Triggers host updates when snapshots change or navigation occurs.
 *
 * @example
 * ```typescript
 * class MyElement extends LitElement {
 *   private _history = new HistoryController(this, system);
 *   render() {
 *     const h = this._history.value;
 *     return html`<button ?disabled=${!h?.canGoBack} @click=${() => h?.goBack()}>Undo</button>`;
 *   }
 * }
 * ```
 */
export class HistoryController implements ReactiveController {
  value: HistoryState | null = null;
  private _unsub?: () => void;

  constructor(
    private _host: ReactiveControllerHost,
    private _system: SingleModuleSystem<any>,
  ) {
    this._host.addController(this);
  }

  hostConnected(): void {
    this.value = buildHistoryState(this._system);
    this._unsub = this._system.onHistoryChange(() => {
      this.value = buildHistoryState(this._system);
      this._host.requestUpdate();
    });
  }

  hostDisconnected(): void {
    this._unsub?.();
    this._unsub = undefined;
  }
}

/**
 * Snapshot-only history helper. Returns the current `HistoryState`
 * once and does not re-fire when history mutates.
 *
 * **Named `getHistory` to match the snapshot semantics.** The Lit
 * adapter intentionally provides a non-reactive variant alongside
 * the reactive `HistoryController` so callers reading history once
 * (e.g. logging the last snapshot before a destroy) don't have to
 * spin up a controller. The previous name `useHistory` collided
 * with the reactive `useHistory` semantics in
 * `@directive-run/{react,vue,svelte,solid}` where the hook IS
 * reactive — same name, opposite contract.
 *
 * For reactive history, use `HistoryController`.
 */
export function getHistory(
  system: SingleModuleSystem<any>,
): HistoryState | null {
  assertSystem("getHistory", system);
  return buildHistoryState(system);
}

/**
 * @deprecated Renamed to `getHistory`. The Lit functional helper is
 * snapshot-only; React/Vue/Svelte/Solid `useHistory` is reactive.
 * Use `HistoryController` for the reactive Lit equivalent. The
 * function-wrapper shape (vs `export const x = y`) ensures the
 * `@deprecated` JSDoc tag renders in every TS-aware editor.
 */
export function useHistory(
  system: SingleModuleSystem<any>,
): HistoryState | null {
  return getHistory(system);
}

export function getDerived<T>(
  system: SingleModuleSystem<any>,
  derivationId: string,
): () => T {
  return () => system.read(derivationId) as T;
}

export function getFact<T>(
  system: SingleModuleSystem<any>,
  factKey: string,
): () => T | undefined {
  return () => system.facts.$store.get(factKey) as T | undefined;
}

// ============================================================================
// Typed Hooks Factory
// ============================================================================

export function createTypedHooks<M extends ModuleSchema>(): {
  createDerived: <K extends keyof InferDerivations<M>>(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<M>,
    derivationId: K,
  ) => DerivedController<InferDerivations<M>[K]>;
  createFact: <K extends keyof InferFacts<M>>(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<M>,
    factKey: K,
  ) => FactController<InferFacts<M>[K]>;
  useDispatch: (
    system: SingleModuleSystem<M>,
  ) => (event: InferEvents<M>) => void;
  useEvents: (system: SingleModuleSystem<M>) => SingleModuleSystem<M>["events"];
  createWatch: <K extends string>(
    host: ReactiveControllerHost,
    system: SingleModuleSystem<M>,
    key: K,
    callback: (newValue: unknown, previousValue: unknown) => void,
  ) => WatchController<unknown>;
} {
  return {
    createDerived: <K extends keyof InferDerivations<M>>(
      host: ReactiveControllerHost,
      system: SingleModuleSystem<M>,
      derivationId: K,
    ) =>
      createDerived<InferDerivations<M>[K]>(
        host,
        system,
        derivationId as string,
      ),
    createFact: <K extends keyof InferFacts<M>>(
      host: ReactiveControllerHost,
      system: SingleModuleSystem<M>,
      factKey: K,
    ) => createFact<InferFacts<M>[K]>(host, system, factKey as string),
    useDispatch: (system: SingleModuleSystem<M>) => {
      return (event: InferEvents<M>) => {
        system.dispatch(event);
      };
    },
    useEvents: (system: SingleModuleSystem<M>) => system.events,
    createWatch: <K extends string>(
      host: ReactiveControllerHost,
      system: SingleModuleSystem<M>,
      key: K,
      callback: (newValue: unknown, previousValue: unknown) => void,
    ) => createWatch<unknown>(host, system, key, callback),
  };
}

// ============================================================================
// NamespacedSelectorController — select from a NamespacedSystem
// ============================================================================

/**
 * Reactive controller that selects from a NamespacedSystem.
 * Subscribes to specified keys and triggers host updates.
 *
 * @example
 * ```typescript
 * class MyElement extends LitElement {
 *   private token = new NamespacedSelectorController(
 *     this, system, ["auth.token"], (s) => s.facts.auth.token,
 *   );
 *   render() {
 *     return html`<span>${this.token.value}</span>`;
 *   }
 * }
 * ```
 */
export class NamespacedSelectorController<Modules extends ModulesMap, R>
  implements ReactiveController
{
  private host: ReactiveControllerHost;
  private system: NamespacedSystem<Modules>;
  private keys: string[];
  private selector: (system: NamespacedSystem<Modules>) => R;
  private unsubscribe?: () => void;
  value: R;

  constructor(
    host: ReactiveControllerHost,
    system: NamespacedSystem<Modules>,
    keys: string[],
    selector: (system: NamespacedSystem<Modules>) => R,
  ) {
    this.host = host;
    this.system = system;
    this.keys = keys;
    this.selector = selector;
    this.value = selector(system);
    host.addController(this);
  }

  hostConnected(): void {
    this.value = this.selector(this.system);
    this.unsubscribe = this.system.subscribe(this.keys, () => {
      this.value = this.selector(this.system);
      this.host.requestUpdate();
    });
  }

  hostDisconnected(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }
}

export function createNamespacedSelector<Modules extends ModulesMap, R>(
  host: ReactiveControllerHost,
  system: NamespacedSystem<Modules>,
  keys: string[],
  selector: (system: NamespacedSystem<Modules>) => R,
): NamespacedSelectorController<Modules, R> {
  return new NamespacedSelectorController(host, system, keys, selector);
}

// ============================================================================
// QuerySystemController — Stable query system with Lit lifecycle
// ============================================================================

/**
 * Lit reactive controller that creates and manages a query system.
 * Starts on connect, destroys on disconnect.
 *
 * @example
 * ```typescript
 * import { LitElement, html } from "lit";
 * import { QuerySystemController } from "@directive-run/lit";
 * import { createQuerySystem } from "@directive-run/query";
 *
 * class MyApp extends LitElement {
 *   private app = new QuerySystemController(this, () =>
 *     createQuerySystem({
 *       facts: { userId: "" },
 *       queries: { user: { key: ..., fetcher: ... } },
 *       autoStart: false,
 *     })
 *   );
 *
 *   render() {
 *     const user = this.app.system.read("user");
 *     return html`<div>${user.data?.name}</div>`;
 *   }
 * }
 * ```
 */
export class QuerySystemController<
  T extends {
    start: () => void;
    destroy: () => void;
    isRunning?: boolean;
    [key: string]: any;
  },
> implements ReactiveController
{
  private _system: T | null = null;
  private factory: () => T;

  constructor(host: ReactiveControllerHost, factory: () => T) {
    this.factory = factory;
    host.addController(this);
  }

  get system(): T {
    if (!this._system) {
      throw new Error(
        "[Directive] QuerySystemController.system is not available. " +
          "Access it only in lifecycle methods or after the element is connected.",
      );
    }
    return this._system;
  }

  hostConnected(): void {
    this._system = this.factory();
    if (!this._system.isRunning) {
      this._system.start();
    }
  }

  hostDisconnected(): void {
    // RFC 0009 follow-up (R18 Tier 2-B): destroyAsync so source
    // unsubscribes complete; fire-and-forget with swallow-catch
    // (Lit's hostDisconnected is sync).
    this._system?.destroyAsync().catch((err: unknown) => {
      if (isDevelopment)
        console.warn("[Directive] destroyAsync rejected during unmount:", err);
    });
    this._system = null;
  }
}

// ============================================================================
// SSR Hydration
// ============================================================================

/**
 * Lit reactive controller for SSR hydration.
 * Accepts a DistributableSnapshot and creates a hydrated system.
 *
 * @example
 * ```typescript
 * class MyElement extends LitElement {
 *   private hydration = new HydrationController(this, serverSnapshot);
 *   private system = this.hydration.createSystem(myModule);
 * }
 * ```
 */
export class HydrationController implements ReactiveController {
  private snapshot: Record<string, unknown> | undefined;
  private systems: SingleModuleSystem<any>[] = [];

  constructor(
    host: ReactiveControllerHost,
    snapshot?: Record<string, unknown>,
  ) {
    this.snapshot = snapshot;
    host.addController(this);
  }

  hostConnected(): void {}

  hostDisconnected(): void {
    // RFC 0009 follow-up (R18 Tier 2-B): destroyAsync per system so
    // source unsubscribes complete; fire-and-forget with swallow-catch
    // (Lit's hostDisconnected is sync).
    for (const system of this.systems) {
      system.destroyAsync().catch((err: unknown) => {
        if (isDevelopment)
          console.warn(
            "[Directive] destroyAsync rejected during unmount:",
            err,
          );
      });
    }
    this.systems = [];
  }

  createSystem<S extends ModuleSchema>(
    moduleDef: ModuleDef<S>,
    config?: {
      plugins?: Plugin<any>[];
      trace?: TraceOption;
      errorBoundary?: ErrorBoundaryConfig;
      tickMs?: number;
      zeroConfig?: boolean;
      initialFacts?: Record<string, any>;
    },
  ): SingleModuleSystem<S> {
    const mergedFacts = mergeHydrationFacts(
      this.snapshot,
      config?.initialFacts,
    );
    const system = createSystem({
      module: moduleDef,
      plugins: config?.plugins,
      trace: config?.trace,
      errorBoundary: config?.errorBoundary,
      tickMs: config?.tickMs,
      zeroConfig: config?.zeroConfig,
      initialFacts: mergedFacts,
    } as any) as unknown as SingleModuleSystem<S>;

    this.systems.push(system);

    if (typeof window !== "undefined") {
      system.start();
    }

    return system;
  }
}

// ============================================================================
// AuditLedgerController — live ledger entries with filter (R4.I parity)
// ============================================================================

import type { AuditEntry, AuditLedger, QueryFilter } from "@directive-run/core";

/**
 * Reactive controller exposing the latest audit-ledger entries matching
 * `filter` as `.value`. Polls (default 250 ms — override with `pollMs`)
 * and re-renders the host on each tick that brings new matches.
 *
 * **Polling floor:** `pollMs` is clamped to a minimum of 50 ms to prevent
 * accidental DoS from typos like `pollMs: 5`. Values below the floor are
 * silently clamped in production; dev mode logs a warning.
 *
 * @example
 * ```ts
 * import { AuditLedgerController } from "@directive-run/lit";
 *
 * class AuditLog extends LitElement {
 *   private ledgerCtrl = new AuditLedgerController(
 *     this,
 *     ledger,
 *     { kind: "constraint.evaluate", limit: 20 },
 *   );
 *
 *   render() {
 *     return html`<ul>
 *       ${this.ledgerCtrl.value.map(e => html`<li>${e.kind} @ ${e.ts}</li>`)}
 *     </ul>`;
 *   }
 * }
 * ```
 */
export class AuditLedgerController implements ReactiveController {
  private host: ReactiveControllerHost;
  private ledger: AuditLedger;
  private filter: QueryFilter;
  private pollMs: number;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Latest matching audit entries. Updates on each poll tick when the
   * seq-window (first/last entry seq) changes. Lit `requestUpdate()` is
   * only called when the window moves — in-place tombstone replacement
   * within the visible window does NOT trigger re-render (use the ledger's
   * onWrite hook for revision-level updates when added).
   */
  value: readonly AuditEntry[];

  constructor(
    host: ReactiveControllerHost,
    ledger: AuditLedger,
    filter: QueryFilter = {},
    opts: { pollMs?: number } = {},
  ) {
    this.host = host;
    this.ledger = ledger;
    this.filter = filter;
    const requestedPollMs = opts.pollMs ?? 250;
    this.pollMs = Math.max(50, requestedPollMs);

    if (isDevelopment) {
      if (opts.pollMs !== undefined && opts.pollMs < 50) {
        console.warn(
          `[Directive] AuditLedgerController pollMs=${opts.pollMs} is below the 50 ms floor; clamped to 50 ms.`,
        );
      }
      if (this.pollMs < 100) {
        const entryCount = ledger.toJSON().entries.length;
        if (entryCount > 1000) {
          console.warn(
            `[Directive] AuditLedgerController polling every ${this.pollMs} ms against a ${entryCount}-entry ledger — that's a lot of CPU per tick. Consider raising pollMs or tightening the filter.`,
          );
        }
      }
    }

    this.value = ledger.query(filter);
    host.addController(this);
  }

  hostConnected(): void {
    this.intervalId = setInterval(() => {
      const next = this.ledger.query(this.filter);
      // Always replace the array reference so consumers reading
      // `.value` see the latest snapshot, but only request a render
      // when the seq-window actually moved. At steady-state capacity
      // (e.g. 10k entries rolling over) the length is constant — using
      // length-diff alone misses every change. Compare the first and
      // last seq instead. (R4.C7)
      const moved =
        next[0]?.seq !== this.value[0]?.seq ||
        next[next.length - 1]?.seq !== this.value[this.value.length - 1]?.seq;
      this.value = next;
      if (moved) {
        this.host.requestUpdate();
      }
    }, this.pollMs);
  }

  hostDisconnected(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
